import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Card, Dropdown, Space, Tooltip, Typography, Button, Slider } from 'antd';
import AntFilePreview from '@/components/ui/AntFilePreview';
import AntImagePreview from '@/components/ui/AntImagePreview';
import {
    CommentOutlined,
    FileOutlined,
    LinkOutlined,
    CloudDownloadOutlined,
    InfoCircleOutlined,
    PlayCircleOutlined,
    CaretRightOutlined,
    PauseOutlined,
    EditOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { useTheme } from 'styled-components';
import Markdown from '@/components/ui/Markdown';
import { MessageActionsPanel } from './MessageActionsPanel';
import { ChatAttachmentCard } from './ChatAttachmentCard';
import {
    buildChatMarkdownComponents,
    ChatMessageLink,
    CodeBlock,
    FencedCodeWithClickableUrls,
} from './chatMessageMarkdownComponents';
import { isCodeLikeChatMessage, formatCodeLikeChatMessage } from '@utils/chatMessageCodeLike.util';
import { resolveChatMediaUrl } from '@utils/chatMediaUrl.util';
import { isLikelyImageAttachment } from '@utils/chatAttachmentKind.util';
import { getAttachmentPreviewImageRaw, getChatFileInlinePreviewMode } from '@utils/chatAttachmentPreview.util';
import { downloadFileViaAnchor } from '@utils/downloadViaAnchor.util';
import { splitPlainTextWithUrls, trimTrailingPunctuation, normalizePlainUrlCandidateToHref } from '@utils/chatPlainTextLinkify.util';
import { isChatBodyPlainForDisplay } from '@utils/chatMessagePlainBody.util';
import { getRemoteFaviconUrl } from '@utils/siteFaviconUrl.util';
import { getAvatarColor } from '@utils/avatarColor';
import ChatCircleAvatar from '@/components/ui/ChatCircleAvatar';

function isVideoMime(mime) {
    return (mime || '').startsWith('video/');
}
function isAudioMime(mime) {
    return (mime || '').startsWith('audio/');
}

/** Voice recordings are often `voice.webm` (audio in a WebM container) — never treat as video. */
function isVoiceLikeFilename(name) {
    return /^voice\./i.test(String(name || '').trim());
}

function isVoiceOrAudioAttachment(name, mime, messageType) {
    if (messageType === 'voice') return true;
    if (String(mime || '').toLowerCase().startsWith('audio/')) return true;
    return isVoiceLikeFilename(name);
}

/** Backend may send octet-stream; `.webm` is often voice audio, not a video file. */
function isVideoByFilename(name, url, messageType) {
    if (messageType === 'voice' || isVoiceLikeFilename(name)) return false;
    const s = `${String(name || '')} ${String(url || '')}`.toLowerCase();
    return /\.(webm|mp4|mov|mkv|avi|m4v|ogv)(\?|#|$)/i.test(s);
}

/** Avoid showing the same URL twice when the body already contains a markdown/HTML link. */
function normalizeUrlForDedupe(u) {
    if (!u || typeof u !== 'string') return '';
    try {
        const x = new URL(u, 'https://placeholder.local');
        return `${x.protocol}//${x.host}${x.pathname.replace(/\/$/, '')}${x.search}`;
    } catch {
        return u.trim().replace(/\/$/, '');
    }
}

function urlsReferencedInBody(body) {
    const set = new Set();
    if (!body || typeof body !== 'string') return set;
    let m;
    const reMd = /\[[^\]]*\]\((https?:[^)\s]+)\)/gi;
    while ((m = reMd.exec(body))) set.add(normalizeUrlForDedupe(m[1]));
    const reHref = /href=["'](https?:[^"']+)["']/gi;
    while ((m = reHref.exec(body))) set.add(normalizeUrlForDedupe(m[1]));
    const rePlain = /\b(https?:\/\/[^\s]+)/gi;
    while ((m = rePlain.exec(body))) set.add(normalizeUrlForDedupe(trimTrailingPunctuation(m[1])));
    const reWww = /\b(www\.[^\s]+)/gi;
    while ((m = reWww.exec(body))) {
        const h = normalizePlainUrlCandidateToHref(m[1]);
        if (h) set.add(normalizeUrlForDedupe(h));
    }
    return set;
}

/** URL-only attachment (not a file blob) — render as link card below the message. */
function isAttachmentLinkRow(att) {
    if (att?.type === 'link') return true;
    const mime = String(att.mimeType ?? att.type ?? '').toLowerCase();
    const url = String(att.url ?? att.href ?? att.fileUrl ?? '');
    const attName = String(att.filename ?? att.name ?? att.title ?? '');
    if (!/^https?:\/\//i.test(url)) return false;
    if (isVideoByFilename(attName, url, undefined) || mime.startsWith('video/')) return false;
    if (mime.startsWith('image/') || mime.startsWith('audio/')) return false;
    const sizeRaw = att.size;
    const sizeNum = sizeRaw == null || sizeRaw === '' ? 0 : Number(sizeRaw);
    const safeSize = Number.isFinite(sizeNum) ? sizeNum : 0;
    const filePath = String(att.filePath ?? att.file_path ?? '').trim();
    // "Add link" sends placeholder metadata (see chat.service sendMessage): octet-stream, size 0, empty filePath.
    if (
        safeSize === 0 &&
        !filePath &&
        (mime === '' || mime === 'application/octet-stream' || mime === 'binary/octet-stream')
    ) {
        return true;
    }
    if (mime && mime !== 'link' && mime.includes('/')) return false;
    return true;
}

const IMAGE_PREVIEW_SIZE = 120;
/** Thumbnails shrink in narrow panels (e.g. thread drawer); no horizontal scroll. */

function ImageAttachmentPreview({ src, name, filename, size, mimeType, dark, onClick }) {
    const [hover, setHover] = useState(false);
    const detailText = [filename || name, size != null ? `${(size / 1024).toFixed(1)} KB` : '', mimeType || ''].filter(Boolean).join(' · ');
    return (
        <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                position: 'relative',
                width: '100%',
                maxWidth: IMAGE_PREVIEW_SIZE,
                minWidth: 0,
                margin: '0 auto',
                aspectRatio: '1',
                height: 'auto',
                borderRadius: 10,
                overflow: 'hidden',
                background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            <img
                src={src}
                alt={name || ''}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                }}
            />
            {hover && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        transition: 'opacity 0.2s ease',
                    }}
                >
                    <Tooltip
                        title={detailText || 'Details'}
                        mouseEnterDelay={0}
                        styles={{ body: { fontSize: 11, padding: '4px 8px', maxWidth: 200 } }}
                    >
                        <a
                            href={src}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                background: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 16,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <InfoCircleOutlined />
                        </a>
                    </Tooltip>
                    <Tooltip
                        title="Download"
                        mouseEnterDelay={0}
                        styles={{ body: { fontSize: 11, padding: '4px 8px' } }}
                    >
                        <a
                            href={src}
                            download={filename || name || 'attachment'}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 6,
                                background: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 16,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <CloudDownloadOutlined />
                        </a>
                    </Tooltip>
                </div>
            )}
        </div>
    );
}

function VideoAttachmentPreview({ src, name, filename, dark, onClick }) {
    return (
        <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
            onClick={onClick}
            style={{
                position: 'relative',
                width: '100%',
                maxWidth: IMAGE_PREVIEW_SIZE,
                minWidth: 0,
                margin: '0 auto',
                aspectRatio: '1',
                height: 'auto',
                borderRadius: 10,
                overflow: 'hidden',
                background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                cursor: 'pointer',
            }}
        >
            <video
                src={src}
                preload="metadata"
                muted
                playsInline
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.35)',
                    pointerEvents: 'none',
                }}
            >
                <PlayCircleOutlined style={{ fontSize: 40, color: 'rgba(255,255,255,0.95)' }} />
            </div>
        </div>
    );
}

function formatDuration(seconds) {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Many browsers report `Infinity` or 0 for short WebM voice clips until metadata is fully resolved. */
function readHtmlAudioDurationSeconds(el) {
    if (!el) return null;
    const d = el.duration;
    if (Number.isFinite(d) && d > 0 && d !== Infinity) return d;
    try {
        if (el.seekable && el.seekable.length > 0) {
            const end = el.seekable.end(el.seekable.length - 1);
            if (Number.isFinite(end) && end > 0) return end;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function AudioAttachmentPreview({ src, name, dark }) {
    const audioRef = useRef(null);
    const [duration, setDuration] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [sliderDragging, setSliderDragging] = useState(false);
    const [sliderPercent, setSliderPercent] = useState(0);
    const sliderDraggingRef = useRef(false);
    const seekFixStartedRef = useRef(false);
    const decodeFallbackStartedRef = useRef(false);

    const syncDurationFromAudio = useCallback((el) => {
        if (!el) return;
        const sec = readHtmlAudioDurationSeconds(el);
        if (sec != null) setDuration(sec);
    }, []);

    const runSeekDurationFix = useCallback((el) => {
        if (!el || seekFixStartedRef.current) return;
        seekFixStartedRef.current = true;
        const onSeeked = () => {
            seekFixStartedRef.current = false;
            syncDurationFromAudio(el);
            try {
                if (el.paused) el.currentTime = 0;
            } catch {
                /* ignore */
            }
        };
        el.addEventListener('seeked', onSeeked, { once: true });
        try {
            el.currentTime = Number.MAX_SAFE_INTEGER;
        } catch {
            el.removeEventListener('seeked', onSeeked);
            seekFixStartedRef.current = false;
        }
    }, [syncDurationFromAudio]);

    const probeDuration = useCallback(
        (el) => {
            if (!el) return;
            syncDurationFromAudio(el);
            if (readHtmlAudioDurationSeconds(el) != null) return;
            if (seekFixStartedRef.current) return;
            const raw = el.duration;
            if (raw === Infinity || !Number.isFinite(raw)) runSeekDurationFix(el);
        },
        [syncDurationFromAudio, runSeekDurationFix],
    );

    useEffect(() => {
        setDuration(null);
        setCurrentTime(0);
        setPlaying(false);
        setSliderDragging(false);
        setSliderPercent(0);
        sliderDraggingRef.current = false;
        seekFixStartedRef.current = false;
        decodeFallbackStartedRef.current = false;
        const el = audioRef.current;
        if (el) {
            el.pause();
            el.currentTime = 0;
            el.load();
        }
    }, [src]);

    useEffect(() => {
        if (!src) return undefined;
        let cancelled = false;
        const t = window.setTimeout(async () => {
            if (cancelled) return;
            const el = audioRef.current;
            if (readHtmlAudioDurationSeconds(el) != null) return;
            if (decodeFallbackStartedRef.current) return;
            decodeFallbackStartedRef.current = true;
            try {
                const AC = window.AudioContext || window.webkitAudioContext;
                if (!AC) return;
                const ctx = new AC();
                const res = await fetch(src, { mode: 'cors', credentials: 'include' });
                if (!res.ok) return;
                const ab = await res.arrayBuffer();
                const buf = await ctx.decodeAudioData(ab.slice(0));
                if (!cancelled && buf?.duration > 0 && Number.isFinite(buf.duration)) setDuration(buf.duration);
                await ctx.close?.();
            } catch {
                /* CORS, unsupported codec in decode path, etc. */
            }
        }, 450);
        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [src]);

    const a11yLabel = 'Voice message';
    const dur = duration && duration > 0 ? duration : null;
    const progressPercent = dur ? Math.min(100, Math.max(0, (currentTime / dur) * 100)) : 0;
    const sliderValue = sliderDragging ? sliderPercent : progressPercent;

    const togglePlay = useCallback(() => {
        const el = audioRef.current;
        if (!el) return;
        if (playing) el.pause();
        else void el.play().catch(() => {});
    }, [playing]);

    const handleSliderChange = useCallback((value) => {
        sliderDraggingRef.current = true;
        setSliderDragging(true);
        setSliderPercent(value);
    }, []);

    const handleSliderAfterChange = useCallback(
        (value) => {
            const el = audioRef.current;
            if (el && dur) {
                el.currentTime = (value / 100) * dur;
                setCurrentTime(el.currentTime);
            }
            sliderDraggingRef.current = false;
            setSliderDragging(false);
        },
        [dur],
    );

    const muted = dark ? 'rgba(255,255,255,0.5)' : 'rgba(15, 23, 42, 0.45)';
    const pillBg = dark ? 'rgba(255,255,255,0.08)' : '#f4f4f5';
    const pillBorder = dark ? 'rgba(255,255,255,0.1)' : 'rgba(15, 23, 42, 0.08)';

    return (
        <Card
            size="small"
            styles={{ body: { padding: '10px 14px' } }}
            style={{
                width: '100%',
                maxWidth: '100%',
                margin: 0,
                borderRadius: 16,
                background: pillBg,
                borderColor: pillBorder,
                boxShadow: dark ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.05)',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <Tooltip title={playing ? 'Pause' : 'Play'}>
                    <Button
                        type="primary"
                        shape="circle"
                        aria-label={playing ? 'Pause' : 'Play'}
                        icon={playing ? <PauseOutlined /> : <CaretRightOutlined style={{ marginLeft: 2 }} />}
                        onClick={togglePlay}
                        style={{
                            flexShrink: 0,
                            width: 40,
                            height: 40,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    />
                </Tooltip>
                <div style={{ flex: 1, minWidth: 56, paddingTop: 2 }}>
                    <Slider
                        min={0}
                        max={100}
                        step={0.25}
                        value={sliderValue}
                        disabled={!dur}
                        tooltip={{ open: false }}
                        onChange={handleSliderChange}
                        onChangeComplete={handleSliderAfterChange}
                    />
                </div>
                <Typography.Text
                    type="secondary"
                    style={{
                        flexShrink: 0,
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '0.02em',
                        color: muted,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <span style={{ color: dark ? 'rgba(255,255,255,0.92)' : '#0f172a' }}>{formatDuration(currentTime)}</span>
                    <span style={{ margin: '0 4px', opacity: 0.5 }}>/</span>
                    <span>{dur != null ? formatDuration(dur) : '--:--'}</span>
                </Typography.Text>
                <Tooltip title="Download">
                    <Button
                        type="text"
                        size="small"
                        aria-label="Download"
                        icon={<CloudDownloadOutlined />}
                        onClick={() => downloadFileViaAnchor(src, name || 'audio')}
                        style={{ flexShrink: 0, color: muted, width: 36, height: 36 }}
                    />
                </Tooltip>
            </div>
            <audio
                ref={audioRef}
                src={src}
                preload="metadata"
                title={a11yLabel}
                style={{ display: 'none' }}
                onLoadedMetadata={(e) => probeDuration(e.target)}
                onDurationChange={(e) => probeDuration(e.target)}
                onLoadedData={(e) => probeDuration(e.target)}
                onProgress={(e) => probeDuration(e.target)}
                onTimeUpdate={(e) => {
                    if (!sliderDraggingRef.current) setCurrentTime(e.target.currentTime);
                }}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => {
                    setPlaying(false);
                    setCurrentTime(0);
                }}
            >
                <a href={src} target="_blank" rel="noopener noreferrer">
                    {a11yLabel}
                </a>
            </audio>
        </Card>
    );
}

// `ChatAttachmentCard` uses `downloadFileViaAnchor` for the download control.

const isDark = (theme) => theme?.colors?.background?.default === '#141414';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '✅', '❌', '👀', '😉'];

function formatLastReplyTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return `Today at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function LinkSiteFavicon({ pageUrl, dark }) {
    const src = useMemo(() => getRemoteFaviconUrl(pageUrl), [pageUrl]);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        setFailed(false);
    }, [pageUrl]);
    if (!src || failed) {
        return <LinkOutlined style={{ fontSize: 16, flexShrink: 0, opacity: dark ? 0.75 : 0.7 }} />;
    }
    return (
        <img
            src={src}
            alt=""
            width={20}
            height={20}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            style={{
                flexShrink: 0,
                borderRadius: 4,
                objectFit: 'contain',
                background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(2,6,23,0.06)',
                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
            }}
            onError={() => setFailed(true)}
        />
    );
}

/** Who reacted — custom panel (not Ant Menu) so other users stay full-contrast; “You” row + remove. */
function ReactionReactorsDropdown({
    dark,
    emoji,
    count,
    reactors,
    messageId,
    myReactionEmoji,
    onToggleReaction,
    children,
}) {
    const [open, setOpen] = useState(false);

    const panelBg = dark ? '#1e1e20' : '#ffffff';
    const panelBorder = dark ? 'rgba(255,255,255,0.12)' : 'rgba(15, 23, 42, 0.1)';
    const divider = dark ? 'rgba(255,255,255,0.08)' : 'rgba(15, 23, 42, 0.08)';
    const muted = dark ? 'rgba(255,255,255,0.55)' : 'rgba(15, 23, 42, 0.55)';
    const text = dark ? 'rgba(255,255,255,0.94)' : '#0f172a';

    const sorted = [...reactors].sort((a, b) => (a.isMe === b.isMe ? 0 : a.isMe ? -1 : 1));
    const reactionNoun = count === 1 ? 'reaction' : 'reactions';

    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!onToggleReaction) return;
        onToggleReaction({
            messageId,
            emoji,
            reactedByMe: true,
            myReactionEmoji,
        });
        setOpen(false);
    };

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            trigger={['click']}
            placement="topLeft"
            dropdownRender={() => (
                <div
                    style={{
                        borderRadius: 12,
                        border: `1px solid ${panelBorder}`,
                        boxShadow: dark
                            ? '0 10px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)'
                            : '0 10px 38px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)',
                        background: panelBg,
                        overflow: 'hidden',
                        minWidth: 232,
                        maxWidth: 320,
                    }}
                >
                    <div
                        style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${divider}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                        }}
                    >
                        <span aria-hidden style={{ fontSize: 20, lineHeight: 1 }}>
                            {emoji}
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: text, letterSpacing: '-0.01em' }}>
                                {count} {reactionNoun}
                            </span>
                            <span style={{ fontSize: 11, color: muted, lineHeight: 1.35 }}>People who reacted</span>
                        </div>
                    </div>
                    <div role="list" style={{ padding: '6px 0 8px' }}>
                        {sorted.map((u, idx) => {
                            const label = u.isMe ? 'You' : u.display;
                            return (
                                <div
                                    key={`${label}-${idx}`}
                                    role="listitem"
                                    style={{
                                        margin: u.isMe ? '4px 8px' : '0 8px',
                                        padding: u.isMe ? '8px 10px' : '7px 10px',
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        minHeight: 36,
                                        boxSizing: 'border-box',
                                        background: u.isMe
                                            ? dark
                                                ? 'rgba(139,92,246,0.16)'
                                                : 'rgba(139,92,246,0.1)'
                                            : 'transparent',
                                        border: u.isMe
                                            ? `1px solid ${dark ? 'rgba(139,92,246,0.32)' : 'rgba(139,92,246,0.25)'}`
                                            : '1px solid transparent',
                                        color: text,
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 13,
                                            fontWeight: u.isMe ? 600 : 500,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                            color: text,
                                        }}
                                        title={!u.isMe ? String(label) : undefined}
                                    >
                                        {label}
                                    </span>
                                    {u.isMe && onToggleReaction ? (
                                        <button
                                            type="button"
                                            onClick={handleRemove}
                                            style={{
                                                flexShrink: 0,
                                                fontSize: 12,
                                                fontWeight: 500,
                                                padding: '5px 10px',
                                                borderRadius: 8,
                                                border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(15,23,42,0.12)'}`,
                                                background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.95)',
                                                color: text,
                                                cursor: 'pointer',
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            Remove
                                        </button>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        >
            {children}
        </Dropdown>
    );
}

/**
 * Attachments grid + image/file preview modals. Shared by {@link ChatMessage} and the thread parent strip.
 *
 * @param {{ listMaxWidth?: string }} props.listMaxWidth — CSS `max-width` for the attachment column (main chat uses a capped width).
 */
export function ChatMessageAttachmentStack({
    body = '',
    attachments = [],
    messagePreviewImages,
    messageType,
    dark,
    previewContextMeta,
    listMaxWidth = 'min(100%, 400px)',
}) {
    const [attachmentPreview, setAttachmentPreview] = useState({
        open: false,
        items: [],
        index: 0,
        contextMeta: null,
    });

    const bodyUrls = useMemo(() => urlsReferencedInBody(body ?? ''), [body]);

    const attachmentPreviewGalleryItems = useMemo(() => {
        if (!Array.isArray(attachments) || attachments.length === 0) return [];
        const out = [];
        attachments.forEach((att, idx) => {
            const rawUrl = att.url ?? att.fileUrl ?? att.path ?? att.href;
            const dedupKey = normalizeUrlForDedupe(String(rawUrl ?? ''));
            if (isAttachmentLinkRow(att) && dedupKey && bodyUrls.has(dedupKey)) return;

            const previewRaw = getAttachmentPreviewImageRaw(att, idx, messagePreviewImages);
            const mainResolved = rawUrl ? resolveChatMediaUrl(rawUrl) : '';
            const previewResolved = previewRaw ? resolveChatMediaUrl(previewRaw) : '';
            const imageDisplaySrc = previewResolved || mainResolved;
            const name = att.title ?? att.name ?? att.filename ?? 'Attachment';
            const mime = (att.mimeType ?? att.type ?? '').toLowerCase();

            if (isAttachmentLinkRow(att)) return;
            if (!mainResolved && !previewResolved) return;
            if (isVoiceOrAudioAttachment(name, mime, messageType)) return;

            if (
                isLikelyImageAttachment({
                    name,
                    url: rawUrl,
                    mimeType: mime,
                    previewUrl: previewRaw,
                    filePath: att.filePath ?? att.file_path,
                })
            ) {
                out.push({
                    attachmentIndex: idx,
                    displayUrl: imageDisplaySrc,
                    downloadUrl: mainResolved || imageDisplaySrc,
                    name,
                    variant: 'image',
                });
                return;
            }
            if (isVideoMime(mime) || isVideoByFilename(name, rawUrl, messageType)) {
                out.push({
                    attachmentIndex: idx,
                    displayUrl: mainResolved,
                    downloadUrl: mainResolved,
                    name,
                    variant: 'video',
                });
                return;
            }
            const mode = getChatFileInlinePreviewMode({ mimeType: mime, name, url: mainResolved });
            const variant = mode === 'none' ? 'file' : mode;
            out.push({
                attachmentIndex: idx,
                displayUrl: mainResolved,
                downloadUrl: mainResolved,
                name,
                variant,
            });
        });
        return out;
    }, [attachments, messagePreviewImages, bodyUrls, messageType]);

    const openAttachmentPreviewAtAttachmentIndex = useCallback(
        (idx) => {
            const pIdx = attachmentPreviewGalleryItems.findIndex((it) => it.attachmentIndex === idx);
            if (pIdx === -1) return;
            setAttachmentPreview({
                open: true,
                items: attachmentPreviewGalleryItems.map(({ displayUrl, downloadUrl, name, variant }) => ({
                    displayUrl,
                    downloadUrl,
                    name,
                    variant,
                })),
                index: pIdx,
                contextMeta: previewContextMeta ?? null,
            });
        },
        [attachmentPreviewGalleryItems, previewContextMeta],
    );

    const closeAttachmentPreview = useCallback(() => setAttachmentPreview((s) => ({ ...s, open: false })), []);

    const attachmentPreviewIsImageOnly =
        attachmentPreview.open &&
        attachmentPreview.items.length > 0 &&
        attachmentPreview.items.every((i) => i.variant === 'image');

    if (!Array.isArray(attachments) || attachments.length === 0) return null;

    return (
        <>
            <div
                style={{
                    alignSelf: 'flex-start',
                    width: '100%',
                    maxWidth: listMaxWidth,
                    minWidth: 0,
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(88px, 100%), 1fr))',
                        gap: 8,
                        justifyContent: 'stretch',
                        justifyItems: 'stretch',
                        alignItems: 'start',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflowX: 'hidden',
                    }}
                >
                    {attachments.map((att, idx) => {
                        const rawUrl = att.url ?? att.fileUrl ?? att.path ?? att.href;
                        const dedupKey = normalizeUrlForDedupe(String(rawUrl ?? ''));
                        if (isAttachmentLinkRow(att) && dedupKey && bodyUrls.has(dedupKey)) {
                            return null;
                        }
                        const previewRaw = getAttachmentPreviewImageRaw(att, idx, messagePreviewImages);
                        const url = rawUrl ? resolveChatMediaUrl(rawUrl) : '';
                        const previewResolved = previewRaw ? resolveChatMediaUrl(previewRaw) : '';
                        const imageDisplaySrc = previewResolved || url;
                        const name = att.title ?? att.name ?? att.filename ?? 'Attachment';
                        const mime = (att.mimeType ?? att.type ?? '').toLowerCase();
                        if (!url && !previewResolved) {
                            return (
                                <span
                                    key={idx}
                                    style={{
                                        gridColumn: '1 / -1',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontSize: 13,
                                        maxWidth: '100%',
                                    }}
                                >
                                    <FileOutlined /> {name}
                                </span>
                            );
                        }
                        if (
                            isLikelyImageAttachment({
                                name,
                                url: rawUrl,
                                mimeType: mime,
                                previewUrl: previewRaw,
                                filePath: att.filePath ?? att.file_path,
                            })
                        ) {
                            return (
                                <ImageAttachmentPreview
                                    key={idx}
                                    src={imageDisplaySrc}
                                    name={name}
                                    filename={att.filename ?? att.name}
                                    size={att.size}
                                    mimeType={att.mimeType ?? att.type}
                                    dark={dark}
                                    onClick={() => openAttachmentPreviewAtAttachmentIndex(idx)}
                                />
                            );
                        }
                        if (isVoiceOrAudioAttachment(name, mime, messageType)) {
                            return (
                                <div
                                    key={idx}
                                    style={{
                                        gridColumn: '1 / -1',
                                        minWidth: 0,
                                        maxWidth: '100%',
                                        width: '100%',
                                    }}
                                >
                                    <AudioAttachmentPreview src={url} name={name} dark={dark} />
                                </div>
                            );
                        }
                        if (isVideoMime(mime) || isVideoByFilename(name, rawUrl, messageType)) {
                            return (
                                <VideoAttachmentPreview
                                    key={idx}
                                    src={url}
                                    name={name}
                                    filename={att.filename ?? att.name}
                                    dark={dark}
                                    onClick={() => openAttachmentPreviewAtAttachmentIndex(idx)}
                                />
                            );
                        }
                        if (isAttachmentLinkRow(att)) {
                            let hostLabel = '';
                            try {
                                hostLabel = new URL(url).hostname || '';
                            } catch {
                                hostLabel = '';
                            }
                            return (
                                <div
                                    key={idx}
                                    style={{
                                        gridColumn: '1 / -1',
                                        justifySelf: 'stretch',
                                        maxWidth: '100%',
                                        width: '100%',
                                        minWidth: 0,
                                        padding: '10px 12px',
                                        borderRadius: 8,
                                        border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
                                        background: dark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
                                        textAlign: 'left',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 11,
                                            color: dark ? 'rgba(255,255,255,0.45)' : '#64748b',
                                            marginBottom: 6,
                                            fontWeight: 500,
                                        }}
                                    >
                                        Link{hostLabel ? ` · ${hostLabel}` : ''}
                                    </div>
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        draggable={false}
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: dark ? '#93c5fd' : '#2563eb',
                                            textDecoration: 'none',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 8,
                                            minWidth: 0,
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        <LinkSiteFavicon pageUrl={url} dark={dark} />
                                        <span style={{ minWidth: 0 }}>{name}</span>
                                    </a>
                                    <div
                                        style={{
                                            marginTop: 6,
                                            fontSize: 12,
                                            color: dark ? 'rgba(255,255,255,0.5)' : '#64748b',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {url}
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div
                                key={idx}
                                style={{
                                    gridColumn: '1 / -1',
                                    justifySelf: 'stretch',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    width: '100%',
                                }}
                            >
                                <ChatAttachmentCard
                                    attachment={{
                                        ...att,
                                        url: url || rawUrl,
                                        name,
                                        mimeType: mime,
                                    }}
                                    attachmentIndex={idx}
                                    messagePreviewImages={messagePreviewImages}
                                    dark={dark}
                                    onOpen={() => openAttachmentPreviewAtAttachmentIndex(idx)}
                                    onDownload={(a) => {
                                        const targetUrl = resolveChatMediaUrl(a?.url ?? a?.fileUrl ?? a?.path ?? a?.href);
                                        const fname = a?.filename ?? a?.name ?? a?.title ?? name;
                                        downloadFileViaAnchor(targetUrl, fname);
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
            {attachmentPreviewIsImageOnly ? (
                <AntImagePreview
                    open={attachmentPreview.open}
                    onClose={closeAttachmentPreview}
                    zIndex={2000}
                    {...(attachmentPreview.items.length > 1
                        ? {
                              sources: attachmentPreview.items.map((i) => i.displayUrl),
                              startIndex: attachmentPreview.index,
                              onSlideChange: (i) => setAttachmentPreview((s) => ({ ...s, index: i })),
                          }
                        : { src: attachmentPreview.items[0]?.displayUrl })}
                />
            ) : (
                <AntFilePreview
                    open={attachmentPreview.open}
                    onClose={closeAttachmentPreview}
                    items={attachmentPreview.items}
                    index={attachmentPreview.index}
                    onIndexChange={(i) => setAttachmentPreview((s) => ({ ...s, index: i }))}
                    zIndex={2000}
                    contextMeta={attachmentPreview.contextMeta}
                />
            )}
        </>
    );
}

export function ChatMessage({
    messageId,
    userId,
    senderName,
    time,
    body,
    attachments = [],
    messageType,
    initials,
    avatarImageUrl,
    isOwn,
    reactions = [],
    replyCount = 0,
    lastReplyAt = null,
    onReply,
    onAddReaction,
    onToggleReaction,
    onEdit,
    onDelete,
    onForwardSelect,
    addReactionLoading,
    currentUserId,
    isSelected = false,
    edited = false,
    myReactionEmoji = null,
    selectionMode = false,
    /** Optional `previewImages[]` on the message, parallel to `attachments` (same index). */
    messagePreviewImages,
}) {
    const [hover, setHover] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const theme = useTheme();
    const dark = isDark(theme);

    useEffect(() => {
        setIsExpanded(false);
    }, [body]);

    const codeLike = useMemo(() => isCodeLikeChatMessage(body ?? ''), [body]);
    const codeLikeText = useMemo(() => (codeLike ? formatCodeLikeChatMessage(body ?? '') : ''), [body, codeLike]);

    const markdownComponents = useMemo(() => buildChatMarkdownComponents(dark), [dark]);

    const isPlainBody = useMemo(() => isChatBodyPlainForDisplay(body), [body]);
    const shouldCollapsePlain = useMemo(() => {
        const t = String(body ?? '');
        if (codeLike) return false;
        // Never collapse/expand messages that contain fenced code blocks.
        // (Requirement: See more/less should not appear on code blocks.)
        if (/```/.test(t)) return false;
        const lines = t.split('\n').length;
        return t.length > 600 || lines > 8;
    }, [body, codeLike]);

    const avatarSize = 40;
    const avatarColors = useMemo(
        () => getAvatarColor(String(userId ?? messageId ?? '')),
        [userId, messageId],
    );
    const hasActions = onReply || onAddReaction || onEdit || onDelete || onForwardSelect;
    const canSelectionActions = isSelected && (onEdit || onDelete) && isOwn;

    return (
        <div
            style={{
                position: 'relative',
                display: 'flex',
                // Slack-like: both sides render on the left.
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 12,
                padding: '8px 16px',
                borderRadius: 6,
                background: hover
                    ? dark
                        ? 'rgba(255,255,255,0.04)'
                        : 'rgba(0,0,0,0.03)'
                    : 'transparent',
                transition: 'background 0.1s ease',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {/* Floating action bar — top-right on hover */}
            {hasActions && (
                <div
                    style={{
                        position: 'absolute',
                        top: -12,
                        right: 8,
                        zIndex: 2,
                        opacity: hover ? 1 : 0,
                        pointerEvents: hover ? 'auto' : 'none',
                        transition: 'opacity 0.1s ease',
                    }}
                >
                    <MessageActionsPanel
                        messageId={messageId}
                        isOwn={isOwn}
                        onAddReaction={onAddReaction}
                        onReply={onReply}
                        onForwardSelect={onForwardSelect}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        addReactionLoading={addReactionLoading}
                    />
                </div>
            )}

            <ChatCircleAvatar
                imageUrl={avatarImageUrl}
                initials={initials || '?'}
                size={avatarSize}
                avatarBg={avatarColors.bg}
                avatarColor={avatarColors.color}
                style={{ marginTop: 1 }}
            />

            {/* Content */}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    width: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: 4,
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                    MozUserSelect: 'text',
                }}
            >
                {/* Header + body live in one selectable subtree so drag-select spans code / quotes / links. */}
                <div
                    style={{
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: dark ? 'rgba(255,255,255,0.92)' : '#111827',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        alignItems: 'stretch',
                        maxWidth: selectionMode ? 'min(100%, 560px)' : 'min(100%, 720px)',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                    }}
                    className="chat-message-body"
                >
                    {/* Name + time (timestamp directly after name) */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 8,
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            width: '100%',
                            minWidth: 0,
                            userSelect: 'text',
                            WebkitUserSelect: 'text',
                        }}
                    >
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: dark ? '#f9fafb' : '#111827',
                                letterSpacing: '-0.01em',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {senderName}
                        </span>
                        <span
                            style={{
                                fontSize: 12,
                                color: dark ? 'rgba(255,255,255,0.45)' : '#6b7280',
                                fontWeight: 400,
                                flexShrink: 0,
                            }}
                        >
                            {time}
                            {edited ? (
                                <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.75 }}>(edited)</span>
                            ) : null}
                        </span>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            alignItems: 'stretch',
                            minWidth: 0,
                            width: '100%',
                            userSelect: 'text',
                            WebkitUserSelect: 'text',
                            MozUserSelect: 'text',
                        }}
                    >
                    {(body ?? '') && (
                        <div style={{ minWidth: 0, maxWidth: '100%', width: '100%', alignSelf: 'stretch' }}>
                            <div style={{ width: '100%', minWidth: 0, textAlign: 'inherit' }}>
                                <div
                                    style={{
                                        width: '100%',
                                        overflow: shouldCollapsePlain && !isExpanded ? 'hidden' : 'visible',
                                        maxHeight: shouldCollapsePlain && !isExpanded ? 160 : 'none',
                                        position: 'relative',
                                    }}
                                >
                                                {codeLike ? (
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            minWidth: 0,
                                                            alignSelf: 'stretch',
                                                            userSelect: 'text',
                                                            WebkitUserSelect: 'text',
                                                            MozUserSelect: 'text',
                                                        }}
                                                    >
                                                        <CodeBlock dark={dark}>
                                                            <code>
                                                                <FencedCodeWithClickableUrls text={codeLikeText} dark={dark} />
                                                            </code>
                                                        </CodeBlock>
                                                    </div>
                                                ) : isPlainBody ? (
                                                    <div
                                                        style={{
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            width: '100%',
                                                            minWidth: 0,
                                                            userSelect: 'text',
                                                            WebkitUserSelect: 'text',
                                                            MozUserSelect: 'text',
                                                        }}
                                                    >
                                                        {splitPlainTextWithUrls(String(body ?? '')).map((seg, i) =>
                                                            seg.type === 'link' ? (
                                                                <ChatMessageLink
                                                                    key={`u-${i}-${seg.value}`}
                                                                    href={seg.value}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {seg.label ?? seg.value}
                                                                </ChatMessageLink>
                                                            ) : (
                                                                <React.Fragment key={`t-${i}`}>{seg.value}</React.Fragment>
                                                            ),
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Markdown
                                                        source={body}
                                                        component="div"
                                                        style={{
                                                            width: '100%',
                                                            minWidth: 0,
                                                            userSelect: 'text',
                                                            WebkitUserSelect: 'text',
                                                            MozUserSelect: 'text',
                                                        }}
                                                        components={markdownComponents}
                                                    />
                                    )}
                                    {shouldCollapsePlain && !isExpanded ? (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                height: 44,
                                                background: dark
                                                    ? 'linear-gradient(to bottom, rgba(37,37,37,0) 0%, rgba(37,37,37,0.92) 70%, rgba(37,37,37,0.98) 100%)'
                                                    : 'linear-gradient(to bottom, rgba(250,250,250,0) 0%, rgba(250,250,250,0.92) 70%, rgba(250,250,250,0.98) 100%)',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                    ) : null}
                                </div>
                                {shouldCollapsePlain && (
                                    <button
                                        type="button"
                                        onClick={() => setIsExpanded((v) => !v)}
                                        style={{
                                            marginTop: 6,
                                            border: 'none',
                                            background: 'transparent',
                                            color: dark ? '#c4b5fd' : '#6d28d9',
                                            fontWeight: 800,
                                            cursor: 'pointer',
                                            padding: 0,
                                            alignSelf: isOwn ? 'flex-end' : 'flex-start',
                                        }}
                                    >
                                        {isExpanded ? 'See less' : 'See more'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <ChatMessageAttachmentStack
                        body={body}
                        attachments={attachments}
                        messagePreviewImages={messagePreviewImages}
                        messageType={messageType}
                        dark={dark}
                        previewContextMeta={{ senderName, timeLabel: time, initials }}
                        listMaxWidth="min(100%, 400px)"
                    />
                    </div>
                </div>

                {/* Reactions + thread replies + selection actions */}
                {(reactions?.length > 0 || (replyCount > 0 && onReply) || canSelectionActions) && (
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 4,
                        }}
                    >
                        {reactions && reactions.length > 0 && (
                            <>
                                {reactions.map((r, i) => {
                                    const reactedByMe =
                                        (!!currentUserId &&
                                            (Array.isArray(r.userIds)
                                                ? r.userIds.some((id) => String(id) === String(currentUserId))
                                                : false)) ||
                                        (!!currentUserId && myReactionEmoji && String(r.emoji) === String(myReactionEmoji)) ||
                                        (!!currentUserId &&
                                            (Array.isArray(r.users)
                                                ? r.users.some((u) => String(u.userId ?? u.id) === String(currentUserId))
                                                : String(r.userId ?? r.id) === String(currentUserId)));

                                    // Build normalized reactors array used for dropdown menu.
                                    const reactors = (() => {
                                        const list = [];
                                        if (Array.isArray(r.users) && r.users.length > 0) {
                                            for (const u of r.users) {
                                                if (!u) continue;
                                                const userId = u.userId ?? u.id ?? null;
                                                const userName = u.userName ?? u.name ?? null;
                                                const userEmail = u.userEmail ?? u.email ?? null;
                                                const display = userName || userEmail || 'Unknown';
                                                const isMe = !!currentUserId && userId && String(userId) === String(currentUserId);
                                                list.push({ display, isMe });
                                            }
                                            return list;
                                        }
                                        if (Array.isArray(r.userIds) && Array.isArray(r.userNames) && r.userNames.length === r.userIds.length) {
                                            for (let idx = 0; idx < r.userIds.length; idx += 1) {
                                                const id = r.userIds[idx];
                                                const name = r.userNames[idx];
                                                const isMe = !!currentUserId && String(id) === String(currentUserId);
                                                list.push({ display: name || String(id), isMe });
                                            }
                                            return list;
                                        }
                                        if (r.userName || r.userEmail) {
                                            const isMe =
                                                !!currentUserId &&
                                                (Array.isArray(r.userIds)
                                                    ? r.userIds.some((id) => String(id) === String(currentUserId))
                                                    : String(r.userId ?? r.id) === String(currentUserId));
                                            list.push({ display: r.userName || r.userEmail, isMe });
                                            return list;
                                        }
                                        return list;
                                    })();

                                    const count =
                                        typeof r.count === 'number' && r.count > 0
                                            ? r.count
                                            : reactors.length > 0
                                                ? reactors.length
                                                : 1;

                                    const pill = (
                                        <span
                                            style={{
                                                fontSize: 12,
                                                padding: '2px 8px',
                                                borderRadius: 12,
                                                background: reactedByMe
                                                    ? dark
                                                        ? 'rgba(139,92,246,0.36)'
                                                        : 'rgba(139,92,246,0.22)'
                                                    : dark
                                                        ? 'rgba(255,255,255,0.08)'
                                                        : 'rgba(0,0,0,0.05)',
                                                border: `1px solid ${reactedByMe
                                                    ? dark
                                                        ? 'rgba(139,92,246,0.9)'
                                                        : 'rgba(139,92,246,0.75)'
                                                    : dark
                                                        ? 'rgba(255,255,255,0.1)'
                                                        : 'rgba(0,0,0,0.06)'
                                                    }`,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                cursor: reactors.length > 0 && onToggleReaction ? 'pointer' : 'default',
                                                boxShadow: reactedByMe
                                                    ? dark
                                                        ? '0 0 0 2px rgba(139,92,246,0.22)'
                                                        : '0 0 0 2px rgba(139,92,246,0.16)'
                                                    : 'none',
                                            }}
                                        >
                                            <span>{r.emoji}</span>
                                            <span style={{ opacity: 0.8, fontSize: 11 }}>{count}</span>
                                            {reactedByMe ? (
                                                <span
                                                    style={{
                                                        marginLeft: 4,
                                                        fontSize: 10,
                                                        opacity: 0.9,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                    }}
                                                >
                                                    <span>You</span>
                                                </span>
                                            ) : null}
                                        </span>
                                    );

                                    if (!reactors.length) {
                                        return (
                                            <span key={r.emoji + i}>
                                                {pill}
                                            </span>
                                        );
                                    }

                                    return (
                                        <ReactionReactorsDropdown
                                            key={r.emoji + i}
                                            dark={dark}
                                            emoji={r.emoji}
                                            count={count}
                                            reactors={reactors}
                                            messageId={messageId}
                                            myReactionEmoji={myReactionEmoji}
                                            onToggleReaction={onToggleReaction}
                                        >
                                            {pill}
                                        </ReactionReactorsDropdown>
                                    );
                                })}
                                {/* Removed inline "+" add reaction button (use hover action bar instead). */}
                            </>
                        )}

                        {replyCount > 0 && onReply && (
                            <button
                                type="button"
                                onClick={onReply}
                                title="View thread"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    padding: '3px 8px',
                                    border: 'none',
                                    borderRadius: 6,
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    transition: 'background 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = dark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <CommentOutlined style={{ fontSize: 13, color: '#3b82f6' }} />
                                <span style={{ color: '#3b82f6', fontWeight: 500 }}>
                                    {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                                </span>
                                {lastReplyAt && (
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: dark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                        }}
                                    >
                                        · {formatLastReplyTime(lastReplyAt)}
                                    </span>
                                )}
                            </button>
                        )}

                        {canSelectionActions && (
                            <>
                                {onEdit && (
                                    <button
                                        type="button"
                                        title="Edit"
                                        onClick={onEdit}
                                        style={{
                                            width: 26,
                                            height: 26,
                                            borderRadius: 8,
                                            border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                                            background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                                            color: dark ? 'rgba(255,255,255,0.85)' : '#111',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <EditOutlined />
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        type="button"
                                        title="Delete"
                                        onClick={onDelete}
                                        style={{
                                            width: 26,
                                            height: 26,
                                            borderRadius: 8,
                                            border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                                            background: dark ? 'rgba(239,68,68,0.14)' : 'rgba(239,68,68,0.08)',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <DeleteOutlined />
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}
